import React from 'react';
import Grid from '@material-ui/core/Grid';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Container from '@material-ui/core/Container';
import Button from '@material-ui/core/Button';
import Box from '@material-ui/core/Box';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import { OpenInNew, Language as LanguageIcon } from '@material-ui/icons';
import './home.css';
import { useLocalization, Language } from '../../locales';

const languageNames: Record<Language, string> = {
    en: 'English',
    es: 'Español',
    pt: 'Português',
    zh: '中文',
    hi: 'हिन्दी',
    ar: 'العربية',
    ko: '한국어',
    ja: '日本語',
    bn: 'বাংলা',
    de: 'Deutsch',
    fr: 'Français',
};

const Home = () => {
    const { t, language, setLanguage } = useLocalization();

    const handleLanguageChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setLanguage(event.target.value as Language);
    };

    return (
        <div className="page-wrapper">
            {/* Header */}
            <header className="site-header">
                <Container>
                    <Typography variant="h4" component="h1" className="header-title">
                        {t('header.title')}
                    </Typography>
                    <Box style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
                        <nav className="header-nav">
                            <Typography variant="body2" style={{ color: '#C1121F', fontFamily: 'Courier Prime, monospace', letterSpacing: '1px' }}>
                                {t('header.subtitle')}
                            </Typography>
                        </nav>
                        {/* Language Selector */}
                        <Box className="language-toggle" style={{ 
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            backgroundColor: '#111111',
                            padding: '0.5rem 1rem',
                            borderRadius: '4px',
                            border: '1px solid #C1121F'
                        }}>
                            <LanguageIcon style={{ color: '#F4F4F4' }} />
                            <select
                                value={language}
                                onChange={handleLanguageChange}
                                style={{
                                    backgroundColor: '#111111',
                                    color: '#F4F4F4',
                                    border: 'none',
                                    fontFamily: 'Oswald, sans-serif',
                                    fontSize: '0.875rem',
                                    cursor: 'pointer',
                                    outline: 'none'
                                }}
                            >
                                {Object.entries(languageNames).map(([code, name]) => (
                                    <option key={code} value={code}>{name}</option>
                                ))}
                            </select>
                        </Box>
                    </Box>
                </Container>
            </header>

            {/* Breaking News Section - Moved to top */}
            <section className="breaking-news-section" style={{ backgroundColor: '#C1121F', padding: '1rem 0', borderTop: '3px solid #111111', borderBottom: '3px solid #111111' }}>
                <Container>
                    <Box display="flex" flexDirection="column" alignItems="center" style={{ gap: '0.5rem' }}>
                        <Button
                            href="https://www.cnn.com/2026/03/12/health/americans-health-care-expenses-costs"
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ 
                                color: '#F4F4F4', 
                                textAlign: 'center', 
                                fontWeight: 'bold', 
                                fontFamily: 'Oswald, sans-serif', 
                                textTransform: 'uppercase', 
                                letterSpacing: '1px',
                                display: 'block',
                                width: '100%',
                                textDecoration: 'none'
                            }}
                        >
                            <Typography variant="h6" component="span" style={{ color: '#F4F4F4', fontWeight: 'bold', fontFamily: 'Oswald, sans-serif', textTransform: 'uppercase', letterSpacing: '1px' }}>
                                {t('breaking.news')}
                            </Typography>
                        </Button>
                        <Button
                            href="https://www.wsj.com/health/healthcare/shock-and-dismay-among-health-insurers-after-medicare-holds-line-on-2027-payments-3c5bb085?st=DRGH35&reflink=desktopwebshare_permalink"
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                                color: '#F4F4F4',
                                textAlign: 'center',
                                fontWeight: 'bold',
                                fontFamily: 'Oswald, sans-serif',
                                textTransform: 'uppercase',
                                letterSpacing: '1px',
                                display: 'block',
                                width: '100%',
                                textDecoration: 'none',
                                border: '1px solid #F4F4F4'
                            }}
                        >
                            <Typography variant="caption" component="span" style={{ color: '#F4F4F4', fontWeight: 'bold', fontFamily: 'Oswald, sans-serif', textTransform: 'uppercase', letterSpacing: '1px' }}>
                                {t('news.wsj')}
                            </Typography>
                        </Button>
                    </Box>
                </Container>
            </section>

            {/* Main Content */}
            <main className="main-content">
                {/* Kean Betrayal Section - New section highlighting Kean did NOT join the 4 GOP rebels */}
                <section style={{ backgroundColor: '#1a1a1a', padding: '2.5rem 0', borderBottom: '3px solid #C1121F' }}>
                    <Container>
                        <Box style={{ 
                            backgroundColor: '#000000', 
                            border: '3px solid #C1121F',
                            borderRadius: '8px',
                            padding: '2rem',
                            textAlign: 'center'
                        }}>
                            <Typography variant="h4" component="h2" style={{ 
                                color: '#C1121F', 
                                fontFamily: 'Oswald, sans-serif', 
                                fontWeight: 'bold',
                                textTransform: 'uppercase',
                                letterSpacing: '2px',
                                marginBottom: '1.5rem'
                            }}>
                                {t('kean.betrayal.title')}
                            </Typography>
                            <Typography variant="body1" component="p" style={{ 
                                color: '#F4F4F4', 
                                fontFamily: 'Roboto, sans-serif',
                                marginBottom: '1.5rem',
                                lineHeight: 1.7
                            }}>
                                {t('kean.betrayal.text')}
                            </Typography>
                            <Typography variant="h5" component="p" style={{ 
                                color: '#F4F4F4', 
                                fontFamily: 'Oswald, sans-serif',
                                fontWeight: 'bold',
                                fontStyle: 'italic',
                                marginBottom: '1.5rem'
                            }}>
                                {t('kean.betrayal.comparison')}
                            </Typography>
                            <Button
                                variant="contained"
                                href="https://x.com/CongressmanKean"
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ 
                                    backgroundColor: '#C1121F', 
                                    color: '#F4F4F4',
                                    fontFamily: 'Oswald, sans-serif',
                                    fontWeight: 'bold',
                                    padding: '0.75rem 2rem',
                                    textTransform: 'uppercase',
                                    letterSpacing: '1px'
                                }}
                            >
                                {t('kean.betrayal.cta')}
                            </Button>
                        </Box>
                    </Container>
                </section>

                {/* Tsunami Title Section - Moved below breaking news */}
                <section style={{ backgroundColor: '#000000', padding: '2rem 0', borderBottom: '2px solid #C1121F' }}>
                    <Container>
                        <Typography variant="h3" component="h2" style={{ 
                            textAlign: 'center', 
                            color: '#F4F4F4', 
                            fontFamily: 'Oswald, sans-serif', 
                            fontWeight: 'bold',
                            textTransform: 'uppercase',
                            letterSpacing: '2px'
                        }}>
                            {t('context.title')}
                        </Typography>
                    </Container>
                </section>

                {/* Healthcare Context Section - Moved up right after Tsunami title */}
                <section className="healthcare-context-section">
                    <Container>
                        <Card elevation={3} className="context-card">
                            <CardContent>
                                <Typography variant="body1" component="p" className="section-text" style={{ marginBottom: '1rem' }}>
                                    {t('context.intro')}
                                </Typography>

                                <Box className="crisis-highlight" style={{ 
                                    padding: '1.5rem', 
                                    backgroundColor: '#1a1a1a', 
                                    borderLeft: '4px solid #C1121F',
                                    marginBottom: '1rem',
                                    border: '1px solid #4A4A4A'
                                }}>
                                    <Typography variant="h6" component="h4" style={{ marginBottom: '0.75rem', color: '#C1121F', fontFamily: 'Oswald, sans-serif', textTransform: 'uppercase' }}>
                                        {t('context.truth.title')}
                                    </Typography>
                                    <Typography variant="body1" component="p" className="section-text" style={{ marginBottom: '0.5rem', color: '#d0d0d0' }} dangerouslySetInnerHTML={{ __html: t('context.truth.text') }} />
                                </Box>

                                <Typography variant="body1" component="p" className="section-text" style={{ marginBottom: '1rem' }} dangerouslySetInnerHTML={{ __html: t('context.impact') }} />

                                <Typography variant="body1" component="p" className="section-text" style={{ marginBottom: '1rem' }} dangerouslySetInnerHTML={{ __html: t('context.tsunami') }} />

                                <Box className="crisis-highlight" style={{ 
                                    padding: '1.5rem', 
                                    backgroundColor: '#1a1a1a', 
                                    borderLeft: '4px solid #2C3E50',
                                    marginBottom: '1rem',
                                    border: '1px solid #4A4A4A'
                                }}>
                                    <Typography variant="h6" component="h4" style={{ marginBottom: '0.75rem', color: '#F4F4F4', fontFamily: 'Oswald, sans-serif', textTransform: 'uppercase' }}>
                                        {t('context.exposed.title')}
                                    </Typography>
                                    <Typography variant="body1" component="p" className="section-text" style={{ marginBottom: '0.5rem', color: '#d0d0d0' }}>
                                        {t('context.exposed.text')}
                                    </Typography>
                                </Box>

                                <Typography variant="body1" component="p" className="section-text" style={{ marginBottom: '1rem' }}>
                                    {t('context.accountability')}
                                </Typography>

                                <Button 
                                    variant="contained" 
                                    color="primary"
                                    href="https://www.onenj7.org/take-action"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ marginTop: '1rem' }}
                                >
                                    {t('context.cta')}
                                </Button>
                            </CardContent>
                        </Card>
                    </Container>
                </section>

                {/* Hero Section - Quote and subtext - Moved below healthcare context */}
                <section className="hero-section">
                    <Container>
                        <Typography variant="h4" component="p" className="hero-subtext" style={{ fontFamily: 'Oswald, sans-serif', textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '1rem' }}>
                            {t('hero.quote')}
                        </Typography>
                        <Typography variant="h6" component="p" style={{ color: '#d0d0d0', fontFamily: 'Roboto, sans-serif' }}>
                            {t('hero.subtext')}
                        </Typography>
                    </Container>
                </section>

                {/* ACA Subsidy Cuts Section */}
                <section className="aca-section">
                    <Container>
                        <Grid container spacing={4} alignItems="center">
                            <Grid item xs={12} md={6}>
                                <Typography variant="h3" component="h3" className="section-title">
                                    {t('aca.title')}
                                </Typography>
                                <Typography variant="body1" component="p" className="section-text">
                                    {t('aca.text')}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Box className="chart-container">
                                    <div className="premium-chart">
                                        <Typography variant="h6" className="chart-title">{t('aca.chart.title')}</Typography>
                                        <div className="chart-bars">
                                            <div className="chart-bar-group">
                                                <div className="chart-bar bar-2025" />
                                                <Typography variant="caption" className="bar-label">{t('aca.chart.2025')}</Typography>
                                                <Typography variant="caption" className="bar-value">{t('aca.chart.withSubsidies')}</Typography>
                                            </div>
                                            <div className="chart-bar-group">
                                                <div className="chart-bar bar-2026" />
                                                <Typography variant="caption" className="bar-label">{t('aca.chart.2026')}</Typography>
                                                <Typography variant="caption" className="bar-value">{t('aca.chart.increase')}</Typography>
                                            </div>
                                        </div>
                                    </div>
                                </Box>
                            </Grid>
                        </Grid>
                    </Container>
                </section>

                {/* No Kean Day Event Section */}
                <section style={{ backgroundColor: '#111111', padding: '3rem 0', borderTop: '3px solid #C1121F' }}>
                    <Container>
                        <Grid container spacing={4} alignItems="center" justifyContent="center">
                            <Grid item xs={12} md={6}>
                                <Box style={{ 
                                    backgroundColor: '#000000', 
                                    borderRadius: '12px', 
                                    padding: '2rem', 
                                    textAlign: 'center',
                                    border: '2px solid #C1121F'
                                }}>
                                    <Typography variant="h3" style={{ 
                                        color: '#C1121F', 
                                        fontFamily: 'Oswald, sans-serif', 
                                        fontWeight: 'bold',
                                        marginBottom: '0.5rem'
                                    }}>
                                        {t('nokean.title')}
                                    </Typography>
                                    <Typography variant="h4" style={{ 
                                        color: '#F4F4F4', 
                                        fontFamily: 'Oswald, sans-serif', 
                                        fontWeight: 'bold',
                                        marginBottom: '0.5rem'
                                    }}>
                                        {t('nokean.date')}
                                    </Typography>
                                    <Typography variant="h5" style={{ 
                                        color: '#F4F4F4', 
                                        fontFamily: 'Oswald, sans-serif', 
                                        fontWeight: 'bold',
                                        marginBottom: '0.5rem'
                                    }}>
                                        {t('nokean.message')}
                                    </Typography>
                                    <Typography variant="h6" style={{ 
                                        color: '#F4F4F4', 
                                        fontFamily: 'Oswald, sans-serif', 
                                        textTransform: 'uppercase',
                                        letterSpacing: '2px',
                                        marginBottom: '1.5rem'
                                    }}>
                                        {t('nokean.standup')}
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        href="https://x.com/CongressmanKean"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ 
                                            backgroundColor: '#C1121F', 
                                            padding: '0.75rem 1.5rem', 
                                            borderRadius: '0',
                                            display: 'inline-block'
                                        }}
                                    >
                                        <Typography variant="h5" style={{ 
                                            color: '#F4F4F4', 
                                            fontFamily: 'Oswald, sans-serif', 
                                            fontWeight: 'bold'
                                        }}>
                                            {t('nokean.handle')}
                                        </Typography>
                                    </Button>
                                </Box>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Box style={{ textAlign: 'center' }}>
                                    <img 
                                        src="/kean-hiding.jpg" 
                                        alt="Tom Kean Jr. hiding from constituents"
                                        style={{ 
                                            maxWidth: '100%', 
                                            maxHeight: '400px',
                                            height: 'auto',
                                            borderRadius: '10px',
                                            boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
                                            border: '2px solid #C1121F'
                                        }}
                                    />
                                    <Typography variant="caption" style={{ display: 'block', marginTop: '0.5rem', color: '#d0d0d0', fontFamily: 'Courier Prime, monospace' }}>
                                        {t('nokean.caption')}
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>
                    </Container>
                </section>

                {/* Voting Record Section */}
                <section className="voting-record-section" style={{ backgroundColor: '#000000', padding: '4rem 0' }}>
                    <Container>
                        <Typography variant="h3" component="h3" className="section-title" style={{ textAlign: 'center', marginBottom: '2rem' }}>
                            {t('voting.title')}
                        </Typography>
                        
                        <Box style={{ 
                            backgroundColor: '#000000', 
                            borderRadius: '12px', 
                            overflow: 'hidden',
                            maxWidth: '900px',
                            margin: '0 auto 2rem auto',
                            boxShadow: '0 12px 48px rgba(0,0,0,0.8)'
                        }}>
                            <Box style={{ backgroundColor: '#1a365d', padding: '1.25rem', textAlign: 'center' }}>
                                <Typography variant="h4" style={{ color: '#F4F4F4', fontFamily: 'Georgia, serif', fontWeight: 'bold', fontStyle: 'italic' }}>
                                    {t('voting.record.title')}
                                </Typography>
                            </Box>
                            
                            <Box style={{ padding: '2rem', backgroundColor: '#111111' }}>
                                <Box style={{ border: '2px solid #4A4A4A', borderRadius: '8px', padding: '1.5rem', backgroundColor: '#000000' }}>
                                    <Typography variant="h4" style={{ color: '#60a5fa', fontFamily: 'Georgia, serif', fontWeight: 'bold', textAlign: 'center', marginBottom: '0.5rem' }}>
                                        {t('voting.bill.name')}
                                    </Typography>
                                    <Typography variant="body1" style={{ color: '#d0d0d0', textAlign: 'center', marginBottom: '1rem' }}>
                                        {t('voting.bill.congress')}
                                    </Typography>
                                    <Typography variant="h6" style={{ color: '#F4F4F4', fontWeight: 'bold', marginTop: '1rem' }}>
                                        {t('voting.bill.rollcall')}
                                    </Typography>
                                    <Typography variant="body1" style={{ color: '#d0d0d0', marginTop: '0.5rem' }}>
                                        {t('voting.bill.cut1')}
                                    </Typography>
                                    <Typography variant="body1" style={{ color: '#d0d0d0' }}>
                                        {t('voting.bill.cut2')}
                                    </Typography>
                                </Box>
                            </Box>
                            
                            <Grid container style={{ padding: '0 2rem 2rem 2rem' }}>
                                <Grid item xs={12} md={6} style={{ padding: '0.5rem' }}>
                                    <Box style={{ border: '2px solid #60a5fa', borderRadius: '8px', padding: '1.25rem', backgroundColor: '#000000', height: '100%' }}>
                                        <Typography variant="h5" style={{ color: '#60a5fa', fontFamily: 'Oswald, sans-serif', fontWeight: 'bold', textAlign: 'center', marginBottom: '1rem' }}>
                                            {t('voting.breakdown.title')}
                                        </Typography>
                                        <Typography variant="body1" style={{ color: '#F4F4F4', marginBottom: '0.25rem' }}>{t('voting.breakdown.yeas')}</Typography>
                                        <Typography variant="body1" style={{ color: '#F4F4F4', marginBottom: '0.25rem' }}>{t('voting.breakdown.nays')}</Typography>
                                        <Typography variant="body1" style={{ color: '#F4F4F4', marginBottom: '0.25rem' }}>{t('voting.breakdown.present')}</Typography>
                                        <Typography variant="body1" style={{ color: '#F4F4F4', marginBottom: '1rem' }}>{t('voting.breakdown.notvoting')}</Typography>
                                        <Button
                                            href="https://clerk.house.gov/Votes/2025145"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{ 
                                                backgroundColor: '#22c55e', 
                                                color: '#000000', 
                                                fontFamily: 'Oswald, sans-serif', 
                                                fontWeight: 'bold', 
                                                textAlign: 'center',
                                                width: '100%',
                                                padding: '0.5rem 1rem'
                                            }}
                                        >
                                            {t('voting.breakdown.passed')}
                                        </Button>
                                    </Box>
                                </Grid>
                                <Grid item xs={12} md={6} style={{ padding: '0.5rem' }}>
                                    <Box style={{ border: '2px solid #fca5a5', borderRadius: '8px', padding: '1.25rem', backgroundColor: '#000000', textAlign: 'center', height: '100%' }}>
                                        <Typography variant="h5" style={{ color: '#ef4444', fontFamily: 'Oswald, sans-serif', fontWeight: 'bold', marginBottom: '0.25rem' }}>
                                            {t('voting.kean.name')}
                                        </Typography>
                                        <Typography variant="body1" style={{ color: '#d0d0d0', marginBottom: '1rem' }}>
                                            {t('voting.kean.title')}
                                        </Typography>
                                        <Button
                                            href="https://clerk.house.gov/Votes/2025145"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{ 
                                                backgroundColor: '#dc2626', 
                                                color: '#F4F4F4', 
                                                borderRadius: '50%', 
                                                width: '90px', 
                                                height: '90px', 
                                                display: 'flex', 
                                                alignItems: 'center', 
                                                justifyContent: 'center',
                                                margin: '0 auto 1rem auto',
                                                fontFamily: 'Oswald, sans-serif',
                                                fontWeight: 'bold',
                                                fontSize: '1.75rem',
                                                boxShadow: '0 4px 12px rgba(220, 38, 38, 0.4)',
                                                minWidth: 'unset',
                                                padding: '0'
                                            }}
                                        >
                                            {t('voting.kean.vote')}
                                        </Button>
                                        <Typography variant="body1" style={{ color: '#C1121F', fontFamily: 'Oswald, sans-serif', textTransform: 'uppercase', fontWeight: 'bold' }}>
                                            {t('voting.kean.result')}
                                        </Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                            
                            <Box style={{ padding: '1rem', backgroundColor: '#111111', borderTop: '1px solid #333' }}>
                                <Typography variant="caption" style={{ color: '#999', textAlign: 'center', display: 'block' }}>
                                    {t('voting.source')}
                                </Typography>
                            </Box>
                        </Box>

                        <Box display="flex" justifyContent="center" flexWrap="wrap" style={{ gap: '1rem' }}>
                            <Button 
                                variant="contained" 
                                style={{ backgroundColor: '#C1121F', color: '#F4F4F4', fontFamily: 'Oswald, sans-serif', textTransform: 'uppercase', letterSpacing: '1px' }}
                                href="https://clerk.house.gov/Votes/2025145"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <OpenInNew style={{ marginRight: '8px' }} />
                                {t('voting.cta.vote')}
                            </Button>
                            <Button 
                                variant="contained" 
                                style={{ backgroundColor: '#2C3E50', color: '#F4F4F4', fontFamily: 'Oswald, sans-serif', textTransform: 'uppercase', letterSpacing: '1px' }}
                                href="https://www.congress.gov/bill/119th-congress/house-bill/1"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <OpenInNew style={{ marginRight: '8px' }} />
                                {t('voting.cta.bill')}
                            </Button>
                        </Box>
                    </Container>
                </section>

                {/* Medicaid Cuts Section */}
                <section className="medicaid-section">
                    <Container>
                        <Grid container spacing={4} alignItems="center">
                            <Grid item xs={12} md={6}>
                                <Box className="medicaid-visual">
                                    <div className="medicaid-overlay">
                                        <Typography variant="h4" className="overlay-text">
                                            {t('medicaid.visual')}
                                        </Typography>
                                    </div>
                                </Box>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Typography variant="h3" component="h3" className="section-title">
                                    {t('medicaid.title')}
                                </Typography>
                                <Typography variant="body1" component="p" className="section-text">
                                    {t('medicaid.text')}
                                </Typography>
                                <Box style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                                    <Button
                                        variant="text"
                                        href="https://x.com/search?q=%23KeanUnmasked%20%23FollowTheMoney%20%23RepublicansDontCareAboutYourHealthcare"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ color: '#C1121F', fontFamily: 'Courier Prime, monospace', textTransform: 'none', padding: '0.25rem 0.5rem' }}
                                    >
                                        {t('medicaid.hashtags.text')}
                                    </Button>
                                </Box>
                            </Grid>
                        </Grid>
                    </Container>
                </section>

                {/* Medicare Cuts Section */}
                <section className="medicare-section" id="medicare">
                    <Container>
                        <Grid container spacing={4} alignItems="center">
                            <Grid item xs={12} md={6}>
                                <Typography variant="h3" component="h3" className="section-title">
                                    {t('medicare.title')}
                                </Typography>
                                <Typography variant="body1" component="p" className="section-subtext">
                                    {t('medicare.subtext')}
                                </Typography>
                                <ul className="medicare-bullets">
                                    <li>
                                        <Typography variant="body1" component="span" className="section-text">
                                            {t('medicare.bullet1')}
                                        </Typography>
                                    </li>
                                    <li>
                                        <Typography variant="body1" component="span" className="section-text">
                                            {t('medicare.bullet2')}
                                        </Typography>
                                    </li>
                                    <li>
                                        <Typography variant="body1" component="span" className="section-text">
                                            {t('medicare.bullet3')}
                                        </Typography>
                                    </li>
                                </ul>
                                <Button 
                                    variant="contained" 
                                    className="medicare-cta"
                                    href="https://www.onenj7.org/take-action"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {t('medicare.cta')}
                                </Button>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Box 
                                    className="medicare-visual" 
                                    role="img" 
                                    aria-label="Medicare is a lifeline for seniors"
                                >
                                    <div className="medicare-overlay">
                                        <Typography variant="h4" className="overlay-text">
                                            {t('medicare.visual')}
                                        </Typography>
                                        <img
                                            src="https://api.qrserver.com/v1/create-qr-code/?data=https%3A%2F%2Fwww.onenj7.org%2Ftake-action&size=600x600&margin=20"
                                            alt="QR code linking to One NJ7 Take Action"
                                            width="300"
                                            height="300"
                                            style={{ marginTop: '1rem' }}
                                        />
                                    </div>
                                </Box>
                            </Grid>
                        </Grid>
                    </Container>
                </section>

                {/* Accountability Section */}
                <section className="accountability-section" id="accountability">
                    <Container>
                        <Card elevation={0} className="accountability-card">
                            <CardContent>
                                <Typography variant="h3" component="h3" className="section-title accountability-title">
                                    {t('accountability.title')}
                                </Typography>
                                <Typography variant="body1" component="p" className="section-text accountability-text">
                                    {t('accountability.text')}
                                </Typography>
                                <Button 
                                    variant="contained" 
                                    className="accountability-cta"
                                    href="https://www.mobilize.us/indivisible/event/771456/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {t('accountability.cta')}
                                </Button>
                            </CardContent>
                        </Card>
                    </Container>
                </section>

                {/* News Sources Section */}
                <section className="news-sources-section" style={{ backgroundColor: '#1a1a1a', padding: '2rem 0', borderTop: '1px solid #4A4A4A' }}>
                    <Container>
                        <Typography variant="h5" component="h3" style={{ marginBottom: '1rem', textAlign: 'center', color: '#F4F4F4', fontFamily: 'Oswald, sans-serif', textTransform: 'uppercase', letterSpacing: '1px' }}>
                            {t('news.title')}
                        </Typography>
                        <Typography variant="body2" component="p" style={{ textAlign: 'center', marginBottom: '1rem', color: '#d0d0d0', fontFamily: 'Courier Prime, monospace' }}>
                            {t('news.updated')}
                        </Typography>
                        <Box display="flex" flexDirection="column" alignItems="center" style={{ gap: '1rem' }}>
                            <Button 
                                variant="outlined" 
                                style={{ borderColor: '#C1121F', color: '#F4F4F4', fontFamily: 'Roboto, sans-serif' }}
                                href="https://www.cbsnews.com/news/aca-subsidies-obamacare-health-insurance-affordable-care-act/"
                                target="_blank"
                                rel="noopener noreferrer"
                                size="small"
                            >
                                <OpenInNew style={{ marginRight: '8px', fontSize: '1rem' }} />
                                {t('news.cbs')}
                            </Button>
                            <Button 
                                variant="outlined" 
                                style={{ borderColor: '#2C3E50', color: '#F4F4F4', fontFamily: 'Roboto, sans-serif' }}
                                href="https://www.wsj.com/health/healthcare/shock-and-dismay-among-health-insurers-after-medicare-holds-line-on-2027-payments-3c5bb085?st=DRGH35&reflink=desktopwebshare_permalink"
                                target="_blank"
                                rel="noopener noreferrer"
                                size="small"
                            >
                                <OpenInNew style={{ marginRight: '8px', fontSize: '1rem' }} />
                                {t('news.wsj')}
                            </Button>
                            <Button 
                                variant="outlined" 
                                style={{ borderColor: '#C1121F', color: '#F4F4F4', fontFamily: 'Roboto, sans-serif' }}
                                href="https://www.bloomberg.com/news/articles/2026-01-29/americans-are-more-worried-about-health-care-costs-than-gas-or-groceries"
                                target="_blank"
                                rel="noopener noreferrer"
                                size="small"
                            >
                                <OpenInNew style={{ marginRight: '8px', fontSize: '1rem' }} />
                                {t('news.bloomberg')}
                            </Button>
                            <Button 
                                variant="outlined" 
                                style={{ borderColor: '#2C3E50', color: '#F4F4F4', fontFamily: 'Roboto, sans-serif' }}
                                href="https://www.cnn.com/2026/03/12/health/americans-health-care-expenses-costs"
                                target="_blank"
                                rel="noopener noreferrer"
                                size="small"
                            >
                                <OpenInNew style={{ marginRight: '8px', fontSize: '1rem' }} />
                                {t('news.cnn')}
                            </Button>
                            <Button 
                                variant="outlined" 
                                style={{ borderColor: '#2C3E50', color: '#F4F4F4', fontFamily: 'Roboto, sans-serif' }}
                                href="https://www.nytimes.com/2025/12/12/us/politics/trump-republicans-obamacare-health-costs.html"
                                target="_blank"
                                rel="noopener noreferrer"
                                size="small"
                            >
                                <OpenInNew style={{ marginRight: '8px', fontSize: '1rem' }} />
                                {t('news.nytimes')}
                            </Button>
                            <Button 
                                variant="outlined" 
                                style={{ borderColor: '#C1121F', color: '#F4F4F4', fontFamily: 'Roboto, sans-serif' }}
                                href="https://www.nytimes.com/2025/12/16/us/politics/obamacare-subsidies-house.html"
                                target="_blank"
                                rel="noopener noreferrer"
                                size="small"
                            >
                                <OpenInNew style={{ marginRight: '8px', fontSize: '1rem' }} />
                                {t('news.nytimes2')}
                            </Button>
                            <Button 
                                variant="outlined" 
                                style={{ borderColor: '#2C3E50', color: '#F4F4F4', fontFamily: 'Roboto, sans-serif' }}
                                href="https://www.nytimes.com/live/2025/12/17/us/trump-news#obamacare-subsidies-house"
                                target="_blank"
                                rel="noopener noreferrer"
                                size="small"
                            >
                                <OpenInNew style={{ marginRight: '8px', fontSize: '1rem' }} />
                                {t('news.nytimes3')}
                            </Button>
                            <Button 
                                variant="outlined" 
                                style={{ borderColor: '#C1121F', color: '#F4F4F4', fontFamily: 'Roboto, sans-serif' }}
                                href="https://www.nytimes.com/2026/02/26/health/obamacare-health-insurance-rollbacks.html"
                                target="_blank"
                                rel="noopener noreferrer"
                                size="small"
                            >
                                <OpenInNew style={{ marginRight: '8px', fontSize: '1rem' }} />
                                {t('news.nytimes4')}
                            </Button>
                        </Box>
                    </Container>
                </section>
            </main>

            {/* Footer */}
            <footer className="site-footer">
                <Container>
                    <Paper elevation={0} className="footer-disclaimer">
                        <Typography variant="body2" className="disclaimer-text" component="div" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                            <span>{t('footer.tagline')}</span>
                            <Button
                                variant="text"
                                href="https://x.com/search?q=%23KeanUnmasked%20%23NoKings%20%23RepublicansDontCareAboutYourHealthcare"
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: '#C1121F', fontFamily: 'Courier Prime, monospace', textTransform: 'none', padding: '0', minWidth: 'auto' }}
                            >
                                {t('footer.hashtags')}
                            </Button>
                        </Typography>
                    </Paper>
                    <div className="footer-links">
                        <Button href="/terms-of-use.html" className="footer-link">
                            <OpenInNew style={{ paddingRight: '0.5em', fontSize: '1rem' }} />
                            {t('footer.terms')}
                        </Button>
                        <Button href="/privacy-policy.html" className="footer-link">
                            <OpenInNew style={{ paddingRight: '0.5em', fontSize: '1rem' }} />
                            {t('footer.privacy')}
                        </Button>
                    </div>
                </Container>
            </footer>
        </div>
    );
};

export default Home;
